/**
 * Module dependencies.
 */

import React from 'react';
import {
  ColumnDef,
  Row,
  RowData,
  SortingState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronDown, ChevronLeft, ChevronRight, ChevronsUpDown } from 'lucide-react';
import { cn } from 'app/lib/utils';

/**
 * Per-column presentation hints.
 */

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    align?: 'left' | 'right';
    className?: string;
  }
}

/**
 * Contiguous-run grouping (display only).
 *
 * Collapses consecutive rows that share the same non-null `key` (in the order
 * they're currently shown) into one expandable summary row — e.g. a streak of
 * reward payouts between two real trades folds into a single line. Grouping
 * never touches the underlying data; it only changes how the rows are drawn.
 */

export type DataTableGroup<T> = {
  /** Group key for a row; consecutive rows with the same non-null key collapse together. `null` never groups. */
  key: (row: T) => string | null;
  /** Minimum run length to collapse (default 2). */
  minSize?: number;
  /** Cells for the collapsed summary row, keyed by column id. Missing columns render blank. */
  summary: (rows: T[]) => Record<string, React.ReactNode>;
};

/**
 * `DataTableProps` type.
 */

export type DataTableProps<T> = {
  columns: ColumnDef<T, any>[];
  data: T[];
  onRowClick?: (row: T) => void;
  /** Extra classes per row, derived from the row's data. */
  rowClassName?: (row: T) => string;
  emptyMessage?: React.ReactNode;
  initialSorting?: SortingState;
  /** Fixed layout + colgroup from column sizes — keeps columns from shifting. */
  fixed?: boolean;
  className?: string;
  /** When set, paginate client-side at this page size and show page controls. */
  pageSize?: number;
  /** Selectable page sizes (defaults to 10 / 25 / 50 / 100). */
  pageSizeOptions?: number[];
  /** Collapse contiguous same-key runs into expandable summary rows. */
  group?: DataTableGroup<T>;
};

/**
 * A drawable item: either a standalone row or a collapsed run of rows.
 */

type DisplayItem<T> = { kind: 'row'; row: Row<T> } | { kind: 'group'; id: string; rows: Row<T>[] };

/**
 * Fold consecutive rows sharing a non-null group key into `group` items; runs
 * shorter than `minSize` (and non-keyed rows) stay as standalone `row` items.
 */

function buildContiguousGroups<T>(rows: Row<T>[], group: DataTableGroup<T>): DisplayItem<T>[] {
  const minSize = group.minSize ?? 2;
  const items: DisplayItem<T>[] = [];
  let run: Row<T>[] = [];
  let runKey: string | null = null;

  const flush = () => {
    if (run.length >= minSize && runKey != null) {
      items.push({ id: `grp:${runKey}:${run[0].id}`, kind: 'group', rows: run });
    } else {
      run.forEach(row => items.push({ kind: 'row', row }));
    }

    run = [];
    runKey = null;
  };

  for (const row of rows) {
    const key = group.key(row.original);

    if (key == null) {
      flush();
      items.push({ kind: 'row', row });
      continue;
    }

    if (key === runKey) {
      run.push(row);
    } else {
      flush();
      runKey = key;
      run = [row];
    }
  }

  flush();

  return items;
}

/**
 * Export `DataTable` — a headless (`@tanstack/react-table`) table styled to match
 * the app: uppercase header, sortable columns, row separators, hover and an
 * empty state. Column presentation via `meta.align` / `meta.className`.
 */

export function DataTable<T>({
  columns,
  data,
  onRowClick,
  rowClassName,
  emptyMessage,
  initialSorting,
  fixed,
  className,
  pageSize,
  pageSizeOptions = [10, 25, 50, 100],
  group
}: DataTableProps<T>): JSX.Element {
  const [sorting, setSorting] = React.useState<SortingState>(initialSorting ?? []);
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());
  const [groupPage, setGroupPage] = React.useState(0);
  const [groupPageSize, setGroupPageSize] = React.useState(pageSize ?? 0);

  const table = useReactTable({
    columns,
    data,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // With grouping the rows are paginated by item (a group counts as one), so
    // the built-in pagination model is only wired up when grouping is off.
    ...(pageSize && !group
      ? {
          getPaginationRowModel: getPaginationRowModel(),
          initialState: { pagination: { pageSize } }
        }
      : {})
  });

  const leafColumns = table.getVisibleLeafColumns();

  const toggleGroup = (id: string) =>
    setExpanded(previous => {
      const next = new Set(previous);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });

  // Draw one data row (standalone, or a member of an expanded group).
  const dataRow = (row: Row<T>, keyPrefix = '', extraClassName = '') => (
    <tr
      className={cn('hover:bg-muted/40', onRowClick && 'cursor-pointer', extraClassName, rowClassName?.(row.original))}
      key={`${keyPrefix}${row.id}`}
      onClick={onRowClick ? () => onRowClick(row.original) : undefined}
    >
      {row.getVisibleCells().map(cell => {
        const meta = cell.column.columnDef.meta;

        return (
          <td className={cn('px-3 py-2', meta?.align === 'right' && 'text-right', meta?.className)} key={cell.id}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        );
      })}
    </tr>
  );

  // Draw a collapsed group's summary row (click to expand/collapse). The toggle
  // chevron rides in the first visible column, ahead of that column's summary.
  const groupRow = (item: Extract<DisplayItem<T>, { kind: 'group' }>) => {
    const isOpen = expanded.has(item.id);
    const summary = group ? group.summary(item.rows.map(row => row.original)) : {};

    return (
      <tr
        className={'cursor-pointer bg-muted/30 font-medium hover:bg-muted/50'}
        key={item.id}
        onClick={() => toggleGroup(item.id)}
      >
        {leafColumns.map((column, index) => {
          const meta = column.columnDef.meta;
          const content = summary[column.id] ?? null;

          return (
            <td className={cn('px-3 py-2', meta?.align === 'right' && 'text-right', meta?.className)} key={column.id}>
              {index === 0 ? (
                <span className={'inline-flex items-center gap-1.5'}>
                  {isOpen ? (
                    <ChevronDown className={'h-3.5 w-3.5 shrink-0 text-muted-foreground'} />
                  ) : (
                    <ChevronRight className={'h-3.5 w-3.5 shrink-0 text-muted-foreground'} />
                  )}

                  {content}
                </span>
              ) : (
                content
              )}
            </td>
          );
        })}
      </tr>
    );
  };

  // ── Grouped rendering: paginate by item, expand in place ──────────────────
  if (group) {
    const items = buildContiguousGroups(table.getSortedRowModel().rows, group);
    const usePages = groupPageSize > 0 && items.length > Math.min(...pageSizeOptions);
    const pageCount = usePages ? Math.max(1, Math.ceil(items.length / groupPageSize)) : 1;
    const page = Math.min(groupPage, pageCount - 1);
    const pageItems = usePages ? items.slice(page * groupPageSize, (page + 1) * groupPageSize) : items;

    return (
      <div className={cn('overflow-x-auto', className)}>
        <table className={cn('w-full border-collapse text-sm', fixed && 'table-fixed')}>
          {fixed && (
            <colgroup>
              {leafColumns.map(column => (
                <col
                  key={column.id}
                  style={column.columnDef.size ? { width: `${column.columnDef.size}px` } : undefined}
                />
              ))}
            </colgroup>
          )}

          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => {
                  const align = header.column.columnDef.meta?.align;
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();

                  return (
                    <th
                      className={cn(
                        'border-b-2 border-[#e2e5e9] bg-[#eef1f4] px-3 py-3 text-left text-[11px] font-bold uppercase tracking-[0.06em] text-[#334155]',
                        align === 'right' && 'text-right'
                      )}
                      key={header.id}
                    >
                      {header.isPlaceholder ? null : canSort ? (
                        <button
                          className={cn(
                            'inline-flex items-center gap-1 transition-colors hover:text-foreground',
                            align === 'right' && 'flex-row-reverse'
                          )}
                          onClick={header.column.getToggleSortingHandler()}
                          type={'button'}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}

                          {sorted === 'asc' ? (
                            <ArrowUp className={'h-3 w-3'} />
                          ) : sorted === 'desc' ? (
                            <ArrowDown className={'h-3 w-3'} />
                          ) : (
                            <ChevronsUpDown className={'h-3 w-3 opacity-40'} />
                          )}
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody className={'divide-y divide-border'}>
            {items.length === 0 && (
              <tr>
                <td className={'px-3 py-10 text-center text-sm text-muted-foreground'} colSpan={leafColumns.length}>
                  {emptyMessage}
                </td>
              </tr>
            )}

            {pageItems.map(item =>
              item.kind === 'row'
                ? dataRow(item.row)
                : [
                    groupRow(item),
                    ...(expanded.has(item.id) ? item.rows.map(row => dataRow(row, `${item.id}:`, 'bg-muted/10')) : [])
                  ]
            )}
          </tbody>
        </table>

        {usePages && (
          <div
            className={
              'flex flex-wrap items-center justify-between gap-3 border-t border-border px-3 py-2 text-xs text-muted-foreground'
            }
          >
            <div className={'flex items-center gap-3'}>
              <span>
                {page * groupPageSize + 1}
                {'–'}
                {Math.min((page + 1) * groupPageSize, items.length)}
                {' / '}
                {items.length}
              </span>

              <select
                className={'rounded-md border border-border bg-background px-1.5 py-1 text-xs text-foreground'}
                onChange={event => {
                  setGroupPageSize(Number(event.target.value));
                  setGroupPage(0);
                }}
                value={groupPageSize}
              >
                {Array.from(new Set([...pageSizeOptions, groupPageSize]))
                  .sort((a, b) => a - b)
                  .map(size => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
              </select>
            </div>

            <div className={'flex items-center gap-1'}>
              <button
                className={
                  'inline-flex h-7 w-7 items-center justify-center rounded-md border border-border transition-colors hover:bg-muted/40 disabled:opacity-40'
                }
                disabled={page <= 0}
                onClick={() => setGroupPage(page - 1)}
                type={'button'}
              >
                <ChevronLeft className={'h-4 w-4'} />
              </button>

              <span className={'px-1'}>
                {page + 1}
                {' / '}
                {pageCount}
              </span>

              <button
                className={
                  'inline-flex h-7 w-7 items-center justify-center rounded-md border border-border transition-colors hover:bg-muted/40 disabled:opacity-40'
                }
                disabled={page >= pageCount - 1}
                onClick={() => setGroupPage(page + 1)}
                type={'button'}
              >
                <ChevronRight className={'h-4 w-4'} />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  const rows = table.getRowModel().rows;
  const totalRows = table.getFilteredRowModel().rows.length;
  const paginated = !!pageSize && totalRows > Math.min(...pageSizeOptions);
  const currentPageSize = table.getState().pagination.pageSize;

  return (
    <div className={cn('overflow-x-auto', className)}>
      <table className={cn('w-full border-collapse text-sm', fixed && 'table-fixed')}>
        {fixed && (
          <colgroup>
            {table.getVisibleLeafColumns().map(column => (
              <col
                key={column.id}
                style={column.columnDef.size ? { width: `${column.columnDef.size}px` } : undefined}
              />
            ))}
          </colgroup>
        )}

        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                const align = header.column.columnDef.meta?.align;
                const canSort = header.column.getCanSort();
                const sorted = header.column.getIsSorted();

                return (
                  <th
                    className={cn(
                      'border-b-2 border-[#e2e5e9] bg-[#eef1f4] px-3 py-3 text-left text-[11px] font-bold uppercase tracking-[0.06em] text-[#334155]',
                      align === 'right' && 'text-right'
                    )}
                    key={header.id}
                  >
                    {header.isPlaceholder ? null : canSort ? (
                      <button
                        className={cn(
                          'inline-flex items-center gap-1 transition-colors hover:text-foreground',
                          align === 'right' && 'flex-row-reverse'
                        )}
                        onClick={header.column.getToggleSortingHandler()}
                        type={'button'}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}

                        {sorted === 'asc' ? (
                          <ArrowUp className={'h-3 w-3'} />
                        ) : sorted === 'desc' ? (
                          <ArrowDown className={'h-3 w-3'} />
                        ) : (
                          <ChevronsUpDown className={'h-3 w-3 opacity-40'} />
                        )}
                      </button>
                    ) : (
                      flexRender(header.column.columnDef.header, header.getContext())
                    )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>

        <tbody className={'divide-y divide-border'}>
          {rows.length === 0 && (
            <tr>
              <td
                className={'px-3 py-10 text-center text-sm text-muted-foreground'}
                colSpan={table.getVisibleLeafColumns().length}
              >
                {emptyMessage}
              </td>
            </tr>
          )}

          {rows.map(row => dataRow(row))}
        </tbody>
      </table>

      {paginated && (
        <div
          className={
            'flex flex-wrap items-center justify-between gap-3 border-t border-border px-3 py-2 text-xs text-muted-foreground'
          }
        >
          <div className={'flex items-center gap-3'}>
            <span>
              {table.getState().pagination.pageIndex * currentPageSize + 1}
              {'–'}
              {Math.min((table.getState().pagination.pageIndex + 1) * currentPageSize, totalRows)}
              {' / '}
              {totalRows}
            </span>

            <select
              className={'rounded-md border border-border bg-background px-1.5 py-1 text-xs text-foreground'}
              onChange={event => table.setPageSize(Number(event.target.value))}
              value={currentPageSize}
            >
              {Array.from(new Set([...pageSizeOptions, currentPageSize]))
                .sort((a, b) => a - b)
                .map(size => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
            </select>
          </div>

          <div className={'flex items-center gap-1'}>
            <button
              className={
                'inline-flex h-7 w-7 items-center justify-center rounded-md border border-border transition-colors hover:bg-muted/40 disabled:opacity-40'
              }
              disabled={!table.getCanPreviousPage()}
              onClick={() => table.previousPage()}
              type={'button'}
            >
              <ChevronLeft className={'h-4 w-4'} />
            </button>

            <span className={'px-1'}>
              {table.getState().pagination.pageIndex + 1}
              {' / '}
              {table.getPageCount()}
            </span>

            <button
              className={
                'inline-flex h-7 w-7 items-center justify-center rounded-md border border-border transition-colors hover:bg-muted/40 disabled:opacity-40'
              }
              disabled={!table.getCanNextPage()}
              onClick={() => table.nextPage()}
              type={'button'}
            >
              <ChevronRight className={'h-4 w-4'} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
